import { useState } from "react";
import { motion } from "framer-motion";
import { V1_FEATURE_CARDS } from "@/data/marketingHomeV1Content";

export default function V1FeatureSection() {
  const [hoveredFeature, setHoveredFeature] = useState(null);

  return (
    <section
      className="relative px-4 py-8 sm:px-8 sm:py-12 overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(245,241,237,0.5) 0%, rgba(250,247,248,0.6) 50%, rgba(255,255,255,0.8) 100%)",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 50% 40% at 30% 20%, rgba(181,82,56,0.03), transparent 60%), radial-gradient(ellipse 40% 50% at 75% 80%, rgba(20,184,166,0.025), transparent 60%)" }}
        aria-hidden
      />
      <div className="relative mx-auto max-w-6xl">
        <div className="grid grid-cols-1 items-start gap-4 sm:gap-6 md:grid-cols-2">
          {V1_FEATURE_CARDS.map((feature, idx) => {
            const preview = feature.cardPreview;
            const isHovered = hoveredFeature === idx;
            const isLead = idx === 0;
            const isWide = idx === 4;

            if (isWide) {
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="v1-gradient-border flex h-auto flex-col overflow-hidden rounded-2xl border border-white/60 bg-white/70 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/[0.06] md:col-span-2 md:h-[280px] sm:rounded-3xl sm:flex-row"
                  onMouseEnter={() => setHoveredFeature(idx)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  <div className="relative flex h-48 min-h-[12rem] flex-1 items-center justify-center overflow-hidden sm:h-auto">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="h-full w-full object-cover transition-transform duration-500"
                      style={isHovered ? { transform: "scale(1.04)" } : {}}
                    />
                    {isHovered && (
                      <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute inset-0 flex items-center justify-center bg-[#2c1815]/40 p-4 backdrop-blur-[6px]"
                      >
                        <div
                          className={`w-full max-w-sm rounded-xl border border-white/30 border-l-[3px] ${preview.accent} bg-white/90 px-4 py-3 shadow-xl backdrop-blur-sm`}
                        >
                          <div className="mb-1.5 flex items-center gap-2">
                            <span className={`h-1.5 w-1.5 rounded-full ${preview.dot}`} />
                            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                              {feature.title}
                            </span>
                          </div>
                          <p className="text-sm leading-snug text-slate-900">&quot;{preview.response}&quot;</p>
                        </div>
                      </motion.div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col justify-center p-6 sm:p-10">
                    <h3 className="mb-3 text-xl font-semibold tracking-tight text-slate-900 sm:mb-4 sm:text-2xl lg:text-3xl">
                      {feature.title}
                    </h3>
                    <p className="text-xs leading-relaxed text-slate-600 sm:text-sm">{feature.desc}</p>
                  </div>
                </motion.div>
              );
            }

            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08, type: "spring", stiffness: 260, damping: 25 }}
                className={`v1-gradient-border flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/60 bg-white/70 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/[0.06] sm:rounded-3xl ${
                  isLead ? "h-auto md:h-[600px]" : "h-auto md:h-[484px]"
                }`}
                onMouseEnter={() => setHoveredFeature(idx)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                {isLead ? (
                  <>
                    <div className="p-6 text-center sm:p-10">
                      <h3 className="mb-3 text-xl font-semibold tracking-tight text-slate-900 sm:mb-4 sm:text-2xl lg:text-3xl">
                        {feature.title}
                      </h3>
                      <p className="text-xs leading-relaxed text-slate-600 sm:text-sm">{feature.desc}</p>
                    </div>
                    <div className="relative flex flex-1 flex-col justify-end px-6 pb-6 sm:px-10 sm:pb-10">
                      <div className="relative h-64 overflow-hidden rounded-xl sm:h-full sm:rounded-2xl">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="h-full w-full object-cover transition-transform duration-500"
                          style={isHovered ? { transform: "scale(1.04)" } : {}}
                        />
                        {isHovered && (
                          <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute inset-0 flex items-center justify-center bg-[#2c1815]/40 p-4 backdrop-blur-[6px]"
                          >
                            <div
                              className={`w-full max-w-sm rounded-xl border border-white/30 border-l-[3px] ${preview.accent} bg-white/90 px-4 py-3 shadow-xl backdrop-blur-sm`}
                            >
                              <div className="mb-1.5 flex items-center gap-2">
                                <span className={`h-1.5 w-1.5 rounded-full ${preview.dot}`} />
                                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                  {feature.title}
                                </span>
                              </div>
                              <p className="text-sm leading-snug text-slate-900">&quot;{preview.response}&quot;</p>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="relative flex h-48 flex-1 items-center justify-center overflow-hidden sm:h-auto">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="h-full w-full object-cover transition-transform duration-500"
                        style={isHovered ? { transform: "scale(1.04)" } : {}}
                      />
                      {isHovered && (
                        <motion.div
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="absolute inset-0 flex items-center justify-center bg-[#2c1815]/40 p-4 backdrop-blur-[6px]"
                        >
                          <div
                            className={`w-full max-w-sm rounded-xl border border-white/30 border-l-[3px] ${preview.accent} bg-white/90 px-4 py-3 shadow-xl backdrop-blur-sm`}
                          >
                            <div className="mb-1.5 flex items-center gap-2">
                              <span className={`h-1.5 w-1.5 rounded-full ${preview.dot}`} />
                              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                {feature.title}
                              </span>
                            </div>
                            <p className="text-sm leading-snug text-slate-900">&quot;{preview.response}&quot;</p>
                          </div>
                        </motion.div>
                      )}
                    </div>
                    <div className="p-6 text-center sm:p-8">
                      <h3 className="mb-2 text-lg font-semibold tracking-tight text-slate-900 sm:mb-3 sm:text-xl lg:text-2xl">
                        {feature.title}
                      </h3>
                      <p className="text-xs leading-relaxed text-slate-600 sm:text-sm">{feature.desc}</p>
                    </div>
                  </>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
